import type { CompanyArticles, CompanyArticleSubject } from "@/modules/companies/client/api/types"
import type { I18nLocale } from "@/modules/i18n/types"

import { useInfiniteQuery } from "@tanstack/react-query"
import { useSearchParams } from "next/navigation"

import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import {
  COMPANY_ARTICLES_QUERY_KEY,
  getCompanyArticles,
} from "@/modules/companies/client/api/endpoints/companyArticles"
import {
  ARTICLE_SUBJECT_TYPES_API,
  ARTICLES_PAGE_SIZE,
  QUERY_CACHE_TIME,
  QUERY_STALE_TIME,
} from "@/modules/companies/client/constants/enums"
import { useCompanyFetch } from "@/modules/companies/client/hooks/useCompanyFetch"
import { stripTagClientSide } from "@/modules/companies/client/utils/stripTagClientSide"
import { DEFAULT_LOCALE } from "@/modules/locales/constants"

export type CompanyArticlesFetch = {
  dataCompanyArticles?: CompanyArticles
  isLoadingCompanyArticles: boolean
  isSuccessCompanyArticles: boolean
  isErrorCompanyArticles: boolean
  // real rendered type => Promise<InfiniteQueryObserverResult<CompanyArticles, unknown>>
  fetchNextPageCompanyArticles: () => Promise<unknown>
}

export const useCompanyArticlesFetch = (): CompanyArticlesFetch => {
  const { dataCompany } = useCompanyFetch()
  const { dataCareerCenter } = useCareerCenterFetch()
  const searchParams = useSearchParams()

  const locale = (dataCompany?.page.locale as I18nLocale) || DEFAULT_LOCALE

  const schoolIds = dataCareerCenter && dataCareerCenter.intranet ? [dataCareerCenter.id] : undefined

  const articlesExclusive = searchParams.has("articlesExclusive")
  let articlesSubject: CompanyArticleSubject[] | undefined

  if (searchParams.has("articlesSubject")) {
    articlesSubject = [ARTICLE_SUBJECT_TYPES_API[searchParams.get("articlesSubject") as string]]
  }

  const {
    data,
    isPending: isLoadingCompanyArticles,
    isSuccess: isSuccessCompanyArticles,
    isError: isErrorCompanyArticles,
    fetchNextPage: fetchNextPageCompanyArticles,
  } = useInfiniteQuery({
    enabled: !!dataCompany?.id,
    gcTime: QUERY_CACHE_TIME,
    getNextPageParam: lastPage => (lastPage as { nextPage: number }).nextPage,
    initialPageParam: 0,
    queryFn: ({ pageParam = 0 }) => {
      return getCompanyArticles({
        params: {
          company_id: dataCompany?.id as string,
          locales: [locale],
          // -1 to respect ui : first card is bigger than others (take the place of two)
          page_limit: pageParam === 0 ? ARTICLES_PAGE_SIZE - 1 : ARTICLES_PAGE_SIZE,
          page_offset: pageParam,
          school_ids: schoolIds,
          subjects: articlesSubject,
          targeted_only: articlesExclusive,
        },
        sanitize: stripTagClientSide,
      })
    },
    queryKey: [COMPANY_ARTICLES_QUERY_KEY, dataCompany?.id, locale, schoolIds, articlesSubject, articlesExclusive],
    staleTime: QUERY_STALE_TIME,
  })

  const dataCompanyArticles: CompanyArticles | undefined = data
    ? ({
        articles: (data.pages as CompanyArticles[])
          .map(page => {
            return [...page.articles]
          })
          .flat(),
        filteredTotal: (data.pages as CompanyArticles[])[0].filteredTotal,
        total: (data.pages as CompanyArticles[])[0].filteredTotal,
      } as CompanyArticles)
    : undefined

  return {
    dataCompanyArticles,
    fetchNextPageCompanyArticles,
    isErrorCompanyArticles,
    isLoadingCompanyArticles,
    isSuccessCompanyArticles,
  }
}
